import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { KeyboardDatePickerController } from '../../../components/organisms/form/controllers/KeyboardDatePickerController';
import { Search } from '@material-ui/icons';
import { useCouponUsageStatistic } from '../hooks/useCouponUsageStatistic';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { formatJstDate, validateStartEnd } from '../../../util';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';

const useStyles = makeStyles((theme: Theme) => ({
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  resultList: {
    marginTop: theme.spacing(3),
  },
  groupButton: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonSearch: {
    width: '232px',
  },
  buttonExport: {
    width: '232px',
    marginLeft: '44px',
  },
}));

export type CouponUsageStatisticType = {
  id: string;
  datetime: string;
  couponCode: string;
  couponName: string;
  numberOfRedemptions: string;
};

export type SearchFormValues = {
  usageStartDate: Date | null;
  usageEndDate: Date | null;
};

export const CouponUsageStatisticListPage: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const props = useCouponUsageStatistic();
  const now = DateTime.now();
  const [datePickerRange, setDatePickerRange] = useState<{ usageStartDate: Date | null; usageEndDate: Date | null }>({
    usageStartDate: now.startOf('month').toJSDate(),
    usageEndDate: now.minus({ days: 1 }).toJSDate(),
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      usageStartDate: now.startOf('month').toJSDate(),
      usageEndDate: now.minus({ days: 1 }).toJSDate(),
    },
  });

  const { handleSubmit, watch } = searchForm;

  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'usageShop',
        label: '利用店舗',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponCode',
        label: 'クーポンコード',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'couponName',
        label: 'クーポン名',
        minWidth: theme.spacing(35),
        align: 'left',
      },
      {
        name: 'usedCount',
        label: '消込数',
        minWidth: theme.spacing(16),
        align: 'right',
      },
      {
        name: 'totalAmount',
        label: '金額',
        minWidth: theme.spacing(16),
        align: 'right',
      },
      {
        name: 'action',
        label: '',
        minWidth: theme.spacing(5),
        align: 'center',
      },
    ],
  };

  const validateRangeDatePicker = (data: SearchFormValues): boolean => {
    const usageStartDate = new Date(data.usageStartDate!);
    const usageEndDate = new Date(data.usageEndDate!);

    const usageStartDateTime = DateTime.fromJSDate(usageStartDate);
    const usageEndDateTime = DateTime.fromJSDate(usageEndDate);

    if (!usageStartDateTime.isValid || !usageEndDateTime.isValid) {
      const key = enqueueSnackbar('表示期間、有効期限、取得期限の日時情報を正しく入力してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    if (data.usageStartDate && data.usageEndDate && !validateStartEnd(usageStartDateTime, usageEndDateTime, true)) {
      const key = enqueueSnackbar('開始日時が前になるように修正してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    return true;
  };

  const onSearch = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onSearch(data);
    setDatePickerRange({
      usageStartDate: data.usageStartDate,
      usageEndDate: data.usageEndDate,
    });
  };

  const onCsvOut = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onCsvOut(data);
  };

  useEffect(() => {
    // init search
    props.onSearch(searchForm.getValues());
  }, []);

  return (
    <MenuHeaderTemplate
      header={{
        title: 'クーポン利用状況集計',
        loading: undefined,
        progressLabel: undefined,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormProvider {...searchForm}>
              <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="usageStartDate"
                        label="利用日時"
                        maxDate={
                          watch('usageEndDate') &&
                          new Date(watch('usageEndDate')!) instanceof Date &&
                          !isNaN(new Date(watch('usageEndDate')!).getTime())
                            ? formatJstDate(new Date(watch('usageEndDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item>
                      <Box sx={{ fontSize: '2rem', height: '24px' }}>-</Box>
                    </Grid>
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="usageEndDate"
                        label="利用日時"
                        minDate={
                          watch('usageStartDate') &&
                          new Date(watch('usageStartDate')!) instanceof Date &&
                          !isNaN(new Date(watch('usageStartDate')!).getTime())
                            ? formatJstDate(new Date(watch('usageStartDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <Button
                          type="submit"
                          variant={'outlined'}
                          size={'medium'}
                          color={'default'}
                          fullWidth
                          endIcon={<Search />}
                          className={classes.buttonSearch}
                          disabled={props.loading || props.csvLoading}
                        >
                          検索
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          color={'secondary'}
                          fullWidth
                          className={classes.buttonExport}
                          onClick={handleSubmit(onCsvOut)}
                          disabled={props.loading || props.csvLoading}
                        >
                          {props.csvLoading ? <CircularProgress size={26} /> : 'CSV出力'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Grid>

          <Grid item xs={12} className={classes.resultList}>
            <TableContainer component={Paper}>
              {props.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!props.data) {
                      return;
                    } else if (!props.data?.nodes || props.data.nodes.length === 0) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return props.data.nodes.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.organizationName}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponCode}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponName}
                          </TableCell>
                          <TableCell component="th" scope="row" align="right">
                            {record.usedCount !== null && record.usedCount !== undefined
                              ? `${Number(record.usedCount).toLocaleString('en-US')}`
                              : ''}
                          </TableCell>
                          <TableCell component="th" scope="row" align="right">
                            {record.totalUsedAmount !== null && record.totalUsedAmount !== undefined
                              ? `${Number(record.totalUsedAmount).toLocaleString('en-US')}円`
                              : ''}
                          </TableCell>
                          <TableCell align="center" scope="row">
                            <Button
                              onClick={() => {
                                const query = new URLSearchParams(history.location.search);
                                query.set(
                                  'usageStartDate',
                                  datePickerRange.usageStartDate
                                    ? formatJstDate(new Date(datePickerRange.usageStartDate!).toISOString())
                                    : ''
                                );
                                query.set(
                                  'usageEndDate',
                                  datePickerRange.usageEndDate
                                    ? formatJstDate(new Date(datePickerRange.usageEndDate!).toISOString())
                                    : ''
                                );
                                query.set('couponName', record?.couponName || '');

                                history.push({
                                  pathname: `/coupon-usage-statistic/${record.couponMasterId}`,
                                  search: query.toString(),
                                });
                              }}
                              type="button"
                              size="small"
                              color="primary"
                            >
                              詳細
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {props.data?.nodes?.length && !props.allAcquired ? (
          <Box mt={2}>
            <ReadMoreButton
              fetchMore={() => {
                props.onFetchMore({
                  usageStartDate: searchForm.getValues('usageStartDate'),
                  usageEndDate: searchForm.getValues('usageEndDate'),
                });
              }}
              loading={props.loading}
            />
          </Box>
        ) : (
          <></>
        )}
      </Container>
    </MenuHeaderTemplate>
  );
};
